import React from 'react';
import { Helmet } from 'react-helmet-async';
import { seoConfig, defaultSEO } from '../seoConfig';

// Fix for window object not being available during SSR
const getCanonicalUrl = (path) => {
  if (typeof window === 'undefined') {
    return path;
  }
  return `${window.location.origin}${path}`;
};

const SEO = ({ page = 'home', additionalMeta = {}, additionalSchema = null }) => {
  const config = seoConfig[page] || defaultSEO;

  // Merge additional meta tags with SEO config
  const metaTags = [
    { name: 'description', content: config.description },
    { name: 'keywords', content: config.keywords },
    { property: 'og:title', content: config.ogTitle || config.title },
    { property: 'og:description', content: config.ogDescription || config.description },
    { property: 'og:image', content: config.ogImage },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: getCanonicalUrl(config.canonical) },
    { property: 'og:site_name', content: 'Innothrust' },
    { name: 'twitter:card', content: config.twitterCard },
    { name: 'twitter:title', content: config.ogTitle || config.title },
    { name: 'twitter:description', content: config.ogDescription || config.description },
    { name: 'twitter:image', content: config.ogImage },
    { name: 'twitter:site', content: config.twitterSite || '@innothrust' },
    { name: 'twitter:creator', content: config.twitterCreator || '@innothrust' },
    { name: 'viewport', content: 'width=device-width, initial-scale=1' },
    { name: 'theme-color', content: '#2563eb' }
  ];

  // Add canonical URL
  const canonicalUrl = getCanonicalUrl(config.canonical);

  // Combine with additional meta tags
  let additionalMetaArray = [];
  if (additionalMeta) {
    if (Array.isArray(additionalMeta)) {
      additionalMetaArray = additionalMeta;
    } else {
      additionalMetaArray = Object.entries(additionalMeta).map(([key, value]) => ({
        name: key,
        content: value
      }));
    }
  }
  const finalMetaTags = [...metaTags, ...additionalMetaArray];

  // Schema.org structured data
  const softwareApplicationSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": config.title,
    "description": config.description,
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web Browser",
    "author": {
      "@type": "Organization",
      "name": "Innothrust",
      "url": "https://innothrust.com"
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "url": canonicalUrl,
    "image": config.ogImage,
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "125"
    }
  };

  const finalSchema = additionalSchema
    ? [softwareApplicationSchema, additionalSchema]
    : [softwareApplicationSchema];

  try {
    return (
      <Helmet>
        {/* Standard SEO tags */}
        <title>{config.title}</title>
        <link rel="canonical" href={canonicalUrl} />

        {/* Meta tags */}
        {finalMetaTags.map((meta, index) => (
          <meta
            key={index}
            name={meta.name}
            property={meta.property}
            content={meta.content}
          />
        ))}

        {/* Structured data */}
        <script type="application/ld+json">
          {JSON.stringify(finalSchema, null, 2)}
        </script>
      </Helmet>
    );
  } catch (error) {
    console.error('SEO Component Error:', error);
    return null;
  }
};

export default SEO;